// src/utils/textUtils.js

/**
 * Normalizes text for comparison by removing diacritical marks, standardizing spaces and hyphens
 */
export const normalizeText = (text) => {
  if (!text) return '';
  
  // Convert to lowercase and trim
  let normalized = text.toLowerCase().trim();
  
  // Remove diacritical marks (accents)
  normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  // Standardize spaces
  normalized = normalized.replace(/\s+/g, ' ');
  
  // Standardize hyphens and dashes
  normalized = normalized.replace(/[-–—]/g, '-');
  
  return normalized;
};

/**
 * Split a name into tokens (individual name parts)
 */
export const tokenizeName = (name) => {
  if (!name) return [];
  return normalizeText(name)
    .split(/[\s-]+/)
    .filter(token => token.length > 0);
};

/**
 * Calculate Levenshtein (edit) distance between two strings
 */
export const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  
  // Create matrix
  const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
  
  // Fill first row and column
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  // Fill the dp matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],    // deletion
          dp[i][j - 1],    // insertion
          dp[i - 1][j - 1] // substitution
        );
      }
    }
  }
  
  return dp[m][n];
};

/**
 * Check for common transliteration patterns
 */
export const checkTransliterationPatterns = (str1, str2) => {
  if (!str1 || !str2) return 0;
  
  // Common transliteration patterns
  const patterns = [
    { from: 'v', to: 'w' },
    { from: 'i', to: 'y' },
    { from: 'c', to: 'k' },
    { from: 'sch', to: 'sh' },
    { from: 'tsch', to: 'ch' },
    { from: 'tz', to: 'z' },
    { from: 'y', to: 'j' },
    { from: 'ch', to: 'kh' },
    { from: 'j', to: 'y' },
    { from: 'ij', to: 'y' },
    { from: 'iy', to: 'y' },
    { from: 'iy', to: 'i' },
    { from: 'g', to: 'h' },
    { from: 'x', to: 'ks' },
    { from: 'zh', to: 'j' }
  ];
  
  // Check for systematic replacements
  let transformedStr1 = str1.toLowerCase();
  
  // Count how many patterns match
  let matchedPatterns = 0;
  let totalPatterns = 0;
  
  for (const pattern of patterns) {
    const regex1 = new RegExp(pattern.from, 'g');
    const regex2 = new RegExp(pattern.to, 'g');
    
    if (transformedStr1.includes(pattern.from)) {
      totalPatterns++;
      
      // Replace pattern and check similarity
      const transformed = transformedStr1.replace(regex1, pattern.to);
      if (transformed === str2.toLowerCase() || 
          levenshteinDistance(transformed, str2.toLowerCase()) <= 2) {
        matchedPatterns++;
      }
    }
    
    if (transformedStr1.includes(pattern.to)) {
      totalPatterns++;
      
      // Replace pattern and check similarity
      const transformed = transformedStr1.replace(regex2, pattern.from);
      if (transformed === str2.toLowerCase() || 
          levenshteinDistance(transformed, str2.toLowerCase()) <= 2) {
        matchedPatterns++;
      }
    }
  }
  
  // Calculate transliteration score (0-1)
  return totalPatterns > 0 ? matchedPatterns / totalPatterns : 0;
};
